import { useState } from 'react'
import { useFetchData } from '../hooks/useFetchData'
import { Shield, Anchor, Plane, Search, Loader, AlertCircle } from 'lucide-react'

export default function Notes() {
  const { data, loading, error } = useFetchData('/data/defence_notes.json')
  const [selectedForce, setSelectedForce] = useState('army')
  const [searchQuery, setSearchQuery] = useState('')

  const forces = {
    army: { name: 'Indian Army', icon: Shield, color: 'text-defense-army' },
    navy: { name: 'Indian Navy', icon: Anchor, color: 'text-defense-navy' },
    airforce: { name: 'Indian Air Force', icon: Plane, color: 'text-defense-airforce' },
    general: { name: 'General Defence', icon: Shield, color: 'text-gray-700' },
  }

  const selectedForceData = data?.forces?.[selectedForce]
  const Icon = forces[selectedForce]?.icon || Shield

  // Filter sections based on search
  const filteredSections = selectedForceData?.sections?.filter(
    (section) =>
      section.heading.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.content.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading notes...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading Notes</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-slide-in">
      <div>
        <h1 className="text-3xl font-bold mb-2">Defence Notes</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Study materials for Indian Armed Forces
        </p>
      </div>

      {/* Force Selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(forces).map(([key, force]) => {
          const ForceIcon = force.icon
          return (
            <button
              key={key}
              onClick={() => setSelectedForce(key)}
              className={`card hover:scale-105 transform transition-all ${
                selectedForce === key ? 'ring-2 ring-primary-600' : ''
              }`}
            >
              <ForceIcon className={`w-8 h-8 ${force.color} mx-auto mb-2`} />
              <div className="text-sm font-semibold text-center">{force.name}</div>
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search in notes..."
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Content */}
      <div className="card">
        <div className="flex items-center mb-6">
          <Icon className={`w-8 h-8 ${forces[selectedForce].color} mr-3`} />
          <h2 className="text-2xl font-bold">{selectedForceData?.title}</h2>
        </div>

        {filteredSections && filteredSections.length > 0 ? (
          <div className="space-y-6">
            {filteredSections.map((section, index) => (
              <div key={index} className="pb-6 border-b border-gray-200 dark:border-gray-700 last:border-0">
                <h3 className="text-xl font-bold mb-3 text-primary-600 dark:text-primary-400">
                  {section.heading}
                </h3>
                <div className="prose dark:prose-invert max-w-none">
                  {section.content.split('\n').map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-gray-700 dark:text-gray-300 mb-2 whitespace-pre-wrap">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchQuery ? 'No results found for your search' : 'No content available'}
          </div>
        )}
      </div>

      {/* Print Button */}
      <button
        onClick={() => window.print()}
        className="btn-secondary w-full md:w-auto no-print"
      >
        Print Notes
      </button>
    </div>
  )
}
